/* src/pages/reports/profesores/AlumnosProfesorPage.tsx */
import { useState } from 'react';
import dayjs from 'dayjs';
import {
  Stack,
  TextField,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  LinearProgress,
  Alert,
  InputAdornment,
  TableSortLabel,
  Button,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useAlumnosProfesorReport } from '../../../hooks/useReportJob';
import { useProfesores } from '../../../hooks/useProfesores';
import { useTranslation } from 'react-i18next';
import { downloadReportFile } from '../../../components/utils/downloadXlsx';

export default function AlumnosProfesorPage() {
  const { t } = useTranslation<'translation'>();
  const today = dayjs().format('YYYY-MM-DD');
  const [desde, setDesde] = useState(today);
  const [hasta, setHasta] = useState('');
  const [selected, setSel] = useState<number>();
  const [jobId, setJobId] = useState<string>();
  const [searchProf, setSP] = useState('');
  const [searchAlu, setSA] = useState('');
  const [sortAsc, setAsc] = useState(true); // ↑ fecha cercana primero

  const { rows: profs } = useProfesores();
  const { run, useStatus } = useAlumnosProfesorReport();
  const { data: status } = useStatus(jobId);

  /* lanzar informe */
  const handleSelect = async (id: number) => {
    setSel(id);
    const { jobId } = await run({ desde, ...(hasta ? { hasta } : {}) });
    setJobId(jobId);
  };

  const handleDownload = async () => {
    if (status?.result?.url) {
      try {
        await downloadReportFile(status.result.url);
      } catch (error) {
        console.error('Error al descargar:', error);
      }
    }
  };

  const profsFiltrados = profs.filter((p) =>
    p.nombre.toLowerCase().includes(searchProf.toLowerCase()),
  );

  /* filas alumnos filtradas + ordenadas */
  const filas =
    status?.result?.rows
      .filter((r) => r.profesorId === selected)
      .filter((r) => r.alumno.toLowerCase().includes(searchAlu.toLowerCase()))
      .sort((a, b) => {
        if (!sortAsc) return a.ultimaReserva < b.ultimaReserva ? 1 : -1;
        return a.ultimaReserva > b.ultimaReserva ? 1 : -1;
      }) ?? [];

  return (
    <Stack spacing={3}>
      <Typography variant="h5">{t('alumnosProfesor.titulo')}</Typography>

      {/* filtros arriba */}
      <Stack direction="row" spacing={2} alignItems="center">
        <TextField
          label={t('alumnosProfesor.filtros.desde')}
          type="date"
          value={desde}
          onChange={(e) => setDesde(e.target.value)}
        />

        <TextField
          label={t('alumnosProfesor.filtros.hasta')}
          type="date"
          value={hasta}
          onChange={(e) => setHasta(e.target.value)}
        />

        <TextField
          size="small"
          placeholder={t<string>('alumnosProfesor.filtros.buscarProfesor')}
          value={searchProf}
          onChange={(e) => setSP(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ ml: 2, width: 260 }}
        />
      </Stack>

      {/* lista profesores */}
      <Paper sx={{ maxHeight: 300, overflowY: 'auto', p: 1 }}>
        <Table size="small">
          <TableBody>
            {profsFiltrados.map((p) => (
              <TableRow
                key={p.id}
                hover
                selected={p.id === selected}
                onClick={() => handleSelect(p.id)}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell>{p.nombre}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      {/* progreso */}
      {status && !status.done && !status.failed && (
        <Stack spacing={1}>
          <LinearProgress variant="determinate" value={status.progress} />
          <Typography variant="body2">
            {t('alumnosProfesor.estado.generando', {
              progress: status.progress,
            })}
          </Typography>
        </Stack>
      )}

      {/* resultado */}
      {status?.done && (
        <>
          <Alert severity="success">
            {t('alumnosProfesor.estado.exito')}{' '}
            <Button
              variant="text"
              size="small"
              onClick={handleDownload}
              sx={{ textTransform: 'none', textDecoration: 'underline', p: 0, minWidth: 'auto' }}
            >
              {t('alumnosProfesor.estado.descargar')}
            </Button>{' '}
            {t('alumnosProfesor.estado.filas', {
              count: status.result!.registros,
            })}
          </Alert>

          {/* NEW: buscador alumnos */}
          <TextField
            size="small"
            placeholder={t<string>('alumnosProfesor.filtros.buscarAlumno')}
            value={searchAlu}
            onChange={(e) => setSA(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ width: 260 }}
          />

          <Paper>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <b>{t('alumnosProfesor.tabla.alumno')}</b>
                  </TableCell>
                  <TableCell>
                    <b>{t('alumnosProfesor.tabla.email')}</b>
                  </TableCell>
                  <TableCell>
                    <b>{t('alumnosProfesor.tabla.emailPadre')}</b>
                  </TableCell>
                  <TableCell>
                    <b>{t('alumnosProfesor.tabla.asignaturas')}</b>
                  </TableCell>
                  <TableCell align="right">
                    <b>{t('alumnosProfesor.tabla.clases')}</b>
                  </TableCell>
                  <TableCell align="right">
                    <b>{t('alumnosProfesor.tabla.horas')}</b>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active
                      direction={sortAsc ? 'asc' : 'desc'}
                      onClick={() => setAsc(!sortAsc)}
                    >
                      {t('alumnosProfesor.tabla.ultimaClase')}
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filas.map((r) => (
                  <TableRow key={r.alumnoId}>
                    <TableCell>{r.alumno}</TableCell>
                    <TableCell>{r.email}</TableCell>
                    <TableCell>{r.emailPadre ?? t('alumnosProfesor.tabla.noEmailPadre')}</TableCell>
                    <TableCell>
                      {r.asignaturas || t('alumnosProfesor.tabla.noEmailPadre')}
                    </TableCell>
                    <TableCell align="right">{r.numClases}</TableCell>
                    <TableCell align="right">{r.horas}</TableCell>
                    <TableCell align="right">{r.ultimaReserva}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}
    </Stack>
  );
}
